'use client'

import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { Icon } from '@src/quince-components/src'
import { PAGE_PATH } from '@src/quince-site-common/src/constants/pageData'
import { addGlobalMessage } from '@src/quince-site-common/src/organisms/appLayout'
import { TOAST_TYPES } from '@src/quince-site-common/src/organisms/appLayout/molecules/globalCommunicationWrapper'
import unifiedPaymentGatewayService from '@src/quince-site-common/src/services/unified-payment-gateway/unified-payment-gateway.service'
import {
  Amex,
  Diners,
  Discover,
  Eftpos,
  Jcb,
  MasterCard,
  UnionPay,
  Visa,
} from '@src/quince-site-integrated/src/assets/payments'
import DeleteConfirmationModal from '@src/quince-site-integrated/src/components/Account/DeleteConfirmationModal'
import { capitalizeAllWords } from '@src/quince-site-integrated/src/utils'

import AccountWrapper from '../../components/Account/AccountWrapper'
import PageHeader from '../../components/Account/PageHeader'

import styles from './payments.module.scss'

const PAYMENT_ICON_MAP = {
  visa: Visa,
  amex: Amex,
  diners: Diners,
  discover: Discover,
  eftpos_au: Eftpos,
  jcb: Jcb,
  mastercard: MasterCard,
  unionpay: UnionPay,
  unknown: null,
}

const isCardExpired = (expiryMonth: number, expiryYear: number): boolean => {
  const currentDate = new Date()
  const cardExpiryDate = new Date(expiryYear, expiryMonth - 1)
  return cardExpiryDate < currentDate
}

interface PaymentsProps {
  data: {
    accountPage: {
      menuList: any
      contact: any
      slug: string
      title: string
    }
  }
}

export interface PaymentMethod {
  cardId: number
  metadata: {
    issuer: keyof typeof PAYMENT_ICON_MAP
    last4DigitsOfCard: string
    expiry?: {
      month: number
      year: number
    }
  }
  isDefault?: boolean
  activeSubscriptions?: number[]
}

interface PaymentMethodCardProps {
  method: PaymentMethod
  onDeleteClick: (cardId: number) => void
}

const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({ method, onDeleteClick }) => {
  const { t } = useTranslation('accountPage')
  const { t: tCommon } = useTranslation('common')
  const { cardId, metadata } = method
  const { issuer = 'unknown', last4DigitsOfCard = '' } = metadata

  const CardIcon = PAYMENT_ICON_MAP[issuer] ?? PAYMENT_ICON_MAP.unknown
  const expiryDate = metadata.expiry ? `${metadata.expiry.month}/${metadata.expiry.year}` : ''
  const cardIsExpired = metadata.expiry ? isCardExpired(metadata.expiry.month, metadata.expiry.year) : false
  const hasActiveSubscriptions = method.activeSubscriptions && method.activeSubscriptions.length > 0

  if (!cardId) {
    return null
  }

  return (
    <div className={styles.paymentCard} key={cardId} data-testid={`payment-card-${cardId}`}>
      <div className={styles.cardContent}>
        <div className={styles.cardInfo}>
          {CardIcon && <Icon iconSvg={CardIcon} className={styles.cardIcon} />}
          <div>
            <span className={styles.cardType} data-testid={`card-type-${cardId}`}>
              {issuer.charAt(0).toUpperCase() + issuer.slice(1)} - {last4DigitsOfCard}
            </span>
            {expiryDate && <div className={styles.cardExpiry} data-testid={`card-expiry-${cardId}`}>{expiryDate}</div>}
          </div>
        </div>
        <div className={styles.badges}>
          {method.isDefault && (
            <div className={styles.defaultBadge} data-testid={`default-badge-${cardId}`}>
              {tCommon('default_payment')}
            </div>
          )}
          {hasActiveSubscriptions && (
            <div className={styles.subscriptionBadge} data-testid={`subscription-badge-${cardId}`}>
              {tCommon('usedForSubscriptions')}
            </div>
          )}
        </div>
      </div>
      {cardIsExpired && (
        <div className={styles.expired} data-testid={`expired-badge-${cardId}`}>
          {tCommon('expired')}
        </div>
      )}
      <div className={styles.actions}>
        <span
          className={styles.delete}
          onClick={() => {
            onDeleteClick(cardId)
          }}
          data-testid={`delete-button-${cardId}`}
        >
          {t('delete')}
        </span>
      </div>
    </div>
  )
}

const Payments: React.FC<PaymentsProps> = ({ data }) => {
  const page = data.accountPage
  const { t } = useTranslation('accountPage')
  const { t: tCommon } = useTranslation('common')
  const router = useRouter()

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isSubscriptionWarningOpen, setIsSubscriptionWarningOpen] = useState(false)
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [isNavigatingToSubscriptions, setIsNavigatingToSubscriptions] = useState(false)

  useEffect(() => {
    const fetchSavedCards = async () => {
      try {
        const cards = await unifiedPaymentGatewayService.getSavedCards()
        setPaymentMethods(cards)
      } catch (error) {
        console.error('Error fetching saved cards:', error)
        addGlobalMessage({
          title: tCommon('error'),
          type: TOAST_TYPES.error,
          description: t('paymentMethodDelErr', { action: t('load') }),
        })
      }
    }
    void fetchSavedCards()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDeleteClick = (cardId: number) => {
    const card = paymentMethods.find(method => method.cardId === cardId)
    const hasActiveSubscriptions = card?.activeSubscriptions && card.activeSubscriptions.length > 0

    setSelectedCardId(cardId)

    if (hasActiveSubscriptions) {
      setIsSubscriptionWarningOpen(true)
    } else {
      setIsConfirmModalOpen(true)
    }
  }

  const handleManageSubscriptions = () => {
    setIsNavigatingToSubscriptions(true)
    router.push(`/${PAGE_PATH.MY_SUBSCRIPTIONS}`)
  }

  const confirmDelete = async () => {
    setFormSubmitted(true)
    try {
      if (!selectedCardId) {
        throw new Error('Invalid card')
      }
      await unifiedPaymentGatewayService.deleteSavedCard(selectedCardId)
      setPaymentMethods(methods => methods.filter(method => method.cardId !== selectedCardId))
      addGlobalMessage({
        title: tCommon('success'),
        type: TOAST_TYPES.success,
        description: t('paymentMethodDeleted'),
      })
    } catch (error) {
      console.error('Error deleting card:', error)
      addGlobalMessage({
        title: tCommon('error'),
        type: TOAST_TYPES.error,
        description: t('paymentMethodDelErr', { action: t('delete') }),
      })
    } finally {
      setFormSubmitted(false)
      setIsConfirmModalOpen(false)
      setSelectedCardId(null)
    }
  }

  return (
    <AccountWrapper menu={page.menuList} contact={page.contact}>
      <PageHeader className="" title={page.title} text="" />
      {paymentMethods.length === 0 ? (
        <div className={styles.noPaymentsMessage} data-testid="no-payments-message">
          {t('noPaymentSaved')}{' '}
        </div>
      ) : (
        <div className={styles.container} data-testid="payments-container">
          {paymentMethods.map(method => (
            <PaymentMethodCard key={method.cardId} method={method} onDeleteClick={handleDeleteClick} />
          ))}
        </div>
      )}
      <DeleteConfirmationModal
        open={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false)
        }}
        onConfirm={confirmDelete}
        message={t('deletConfirmation')}
        formSubmitted={formSubmitted}
      />
      <DeleteConfirmationModal
        open={isSubscriptionWarningOpen}
        onClose={() => {
          if (!isNavigatingToSubscriptions) {
            setIsSubscriptionWarningOpen(false)
            setSelectedCardId(null)
          }
        }}
        onPrimaryAction={handleManageSubscriptions}
        message={t('subscriptionWarningMessage')}
        title={t('subscriptionWarningTitle')}
        primaryButtonText={capitalizeAllWords(t('manageSubscriptions'))}
        buttonLayout="vertical"
        formSubmitted={isNavigatingToSubscriptions}
        isLoading={isNavigatingToSubscriptions}
      />
    </AccountWrapper>
  )
}

export default Payments
